import axios from "api/axios"
import { Button, IconButton } from "components/button"
import {
  ChecklistExistingPicker,
  type TChecklistPickerRow,
} from "components/checklist_dropdown"
import { Input } from "components/input"
import { useSnackbarContext } from "components/snackbar"
import { SFlatContent } from "components/segment/flat"
import { useFormik } from "formik"
import isEqual from "lodash/isEqual"
import { cloneDeep } from "lodash"
import React from "react"
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd"
import { CgUndo } from "react-icons/cg"
import { MdDragIndicator } from "react-icons/md"
import { RiAddFill, RiCloseCircleFill, RiDeleteBin2Fill } from "react-icons/ri"
import { useTranslation } from "react-i18next"
import { useMutation, useQuery } from "react-query"
import styled from "styled-components"
import { v4 as uuidv4 } from "uuid"

type TChecklist = {
  id: number | null
  label: string
  order: number | null
  type: string | null
  required: boolean | null
  fk_appointment_type_id: number | null
  fk_sub_appointment_type_id: number | null
  /** Client-only stable key for new rows; stripped before POST. */
  draftKey?: string
}

type ChecklistsProps = {
  appointmentTypeId: number
  subAppointmentTypeId?: number | null
}

/** Same template on header + each row so columns line up (drag handle, #, label, required, fixed actions). */
/** Label fills available space now that type is hidden in this editor. */
const CHECKLIST_MAIN_GRID = "20px 20px minmax(0, 1fr) 3rem 90px"

const Checklists = ({ appointmentTypeId, subAppointmentTypeId = null }: ChecklistsProps) => {
  const { t } = useTranslation()
  const tBase = "views.admin.appointment-types.components.Checklists"
  const tr = React.useCallback(
    (key: string, options?: Record<string, string | number>) =>
      t(`${tBase}.${key}`, options),
    [t]
  )

  const { showSnackbar } = useSnackbarContext()
  const [values, setValues] = React.useState<TChecklist[]>([])
  const [allChecklists, setAllChecklists] = React.useState<TChecklist[]>([])
  const [initialChecklists, setInitialChecklists] = React.useState<TChecklist[]>([])
  const [showExistingPicker, setShowExistingPicker] = React.useState(false)
  const existingAnchorRef = React.useRef<HTMLDivElement | null>(null)

  const isMatchingContext = React.useCallback(
    (checklist: TChecklist) => {
      const matchesParent = Number(checklist.fk_appointment_type_id) === Number(appointmentTypeId)
      if (!matchesParent) return false
      if (subAppointmentTypeId === null) {
        return checklist.fk_sub_appointment_type_id === null
      }
      return Number(checklist.fk_sub_appointment_type_id) === Number(subAppointmentTypeId)
    },
    [appointmentTypeId, subAppointmentTypeId]
  )

  const checklistsQuery = useQuery<TChecklist[]>(
    ["admin-checklists", appointmentTypeId, subAppointmentTypeId],
    async () =>
      await axios
        .get(`${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/checklist`)
        .then((res) => res.data),
    { refetchOnWindowFocus: false }
  )

  // Hydrate from query cache whenever `data` is available. `onSuccess` alone does not run when
  // this tab unmounts and remounts while React Query serves cached data, which left rows empty.
  React.useEffect(() => {
    const data = checklistsQuery.data
    if (data === undefined) return
    const sortedData = [...data]
      .map((row) => ({
        ...row,
        required: row.required === true ? true : row.required === false ? false : null,
      }))
      .sort((a, b) => {
        if (a.order === null && b.order === null) return 0
        if (a.order === null) return 1
        if (b.order === null) return -1
        return a.order - b.order
      })
    const contextRows = sortedData.filter(isMatchingContext)
    setAllChecklists(sortedData)
    setValues(contextRows)
    setInitialChecklists(contextRows)
  }, [checklistsQuery.data, isMatchingContext])

  const saveChanges = useMutation(
    async (payload: { checklists: TChecklist[] }) =>
      await axios
        .post(`${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/checklist`, payload)
        .then((res) => res.data),
    {
      onSuccess: () => {
        showSnackbar(tr("Checklist settings updated"))
        checklistsQuery.refetch()
      },
    }
  )

  const formik = useFormik({
    initialValues: { checklists: values },
    onSubmit: () => handleSaveAll(),
    enableReinitialize: true,
  })

  const handleFieldChange = (
    index: number,
    key: keyof TChecklist,
    value: string | number | boolean | null
  ) => {
    formik.setValues((currentValues) => {
      const nextValues = cloneDeep(currentValues)
      nextValues.checklists[index][key] = value as never
      return nextValues
    })
  }

  const handleDeleteChecklist = (index: number) => {
    formik.setValues((currentValues) => {
      const nextValues = cloneDeep(currentValues)
      nextValues.checklists.splice(index, 1)
      return nextValues
    })
  }

  const getInitialChecklistById = (id: number | null) => {
    if (id === null) return null
    return initialChecklists.find((checklist) => checklist.id === id) || null
  }

  const handleCancelRowChanges = (index: number) => {
    const currentChecklist = formik.values.checklists[index]
    if (!currentChecklist) return

    if (currentChecklist.id === null) {
      handleDeleteChecklist(index)
      return
    }

    const initialChecklist = getInitialChecklistById(currentChecklist.id)
    if (!initialChecklist) return

    formik.setValues((currentValues) => {
      const nextValues = cloneDeep(currentValues)
      nextValues.checklists[index] = {
        ...nextValues.checklists[index],
        label: initialChecklist.label || "",
        type: initialChecklist.type || "",
        order: initialChecklist.order,
        required: initialChecklist.required,
        fk_appointment_type_id: initialChecklist.fk_appointment_type_id,
        fk_sub_appointment_type_id: initialChecklist.fk_sub_appointment_type_id,
      }
      return nextValues
    })
  }

  const addChecklist = () => {
    formik.setValues((currentValues) => ({
      checklists: [
        ...currentValues.checklists,
        {
          id: null,
          draftKey: uuidv4(),
          label: "",
          order: currentValues.checklists.length + 1,
          type: "",
          required: false,
          fk_appointment_type_id: appointmentTypeId,
          fk_sub_appointment_type_id: subAppointmentTypeId,
        },
      ],
    }))
  }

  const copyFromExistingRows = (sources: TChecklistPickerRow[]) => {
    if (!sources.length) return
    formik.setValues((currentValues) => {
      const next = cloneDeep(currentValues)
      let orderBase = next.checklists.length
      for (const source of sources) {
        if (source.id == null) continue
        orderBase += 1
        next.checklists.push({
          id: null,
          draftKey: uuidv4(),
          label: source.label || "",
          type: "",
          order: orderBase,
          required: source.required === true ? true : source.required === false ? false : null,
          fk_appointment_type_id: appointmentTypeId,
          fk_sub_appointment_type_id: subAppointmentTypeId,
        })
      }
      return next
    })
  }

  const getFluidInputStyle = () => ({
    minWidth: 0,
    width: "100%",
    boxSizing: "border-box" as const,
    borderRadius: "0.375rem",
    padding: "0.125rem",
  })

  const getRowStableKey = (checklist: TChecklist) =>
    checklist.id != null ? `id:${checklist.id}` : `draft:${checklist.draftKey ?? ""}`

  const isChecklistRowDirty = (checklist: TChecklist) => {
    if (checklist.id === null) return true
    const initialChecklist = getInitialChecklistById(checklist.id)
    if (!initialChecklist) return true

    return !isEqual(
      {
        label: checklist.label || "",
        type: checklist.type || "",
        required: checklist.required === true,
        fk_appointment_type_id: checklist.fk_appointment_type_id,
        fk_sub_appointment_type_id: checklist.fk_sub_appointment_type_id,
      },
      {
        label: initialChecklist.label || "",
        type: initialChecklist.type || "",
        required: initialChecklist.required === true,
        fk_appointment_type_id: initialChecklist.fk_appointment_type_id,
        fk_sub_appointment_type_id: initialChecklist.fk_sub_appointment_type_id,
      }
    )
  }

  const isFormDirty = React.useMemo(() => {
    const current = formik.values.checklists
    if (current.length !== initialChecklists.length) return true
    for (let i = 0; i < current.length; i++) {
      const row = current[i]
      const initial = initialChecklists[i]
      if (getRowStableKey(row) !== getRowStableKey(initial)) return true
      if (isChecklistRowDirty(row)) return true
    }
    return false
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.checklists, initialChecklists])

  const handleDragEnd = (result: {
    destination?: { index: number } | null
    source: { index: number }
  }) => {
    if (!result.destination) return
    const from = result.source.index
    const to = result.destination.index
    if (from === to) return
    formik.setValues((currentValues) => {
      const next = cloneDeep(currentValues)
      const [moved] = next.checklists.splice(from, 1)
      next.checklists.splice(to, 0, moved)
      return next
    })
  }

  const handleSaveAll = () => {
    if (!isFormDirty || saveChanges.isLoading) return
    const contextRows = formik.values.checklists.map((checklist, index) => {
      const { draftKey: _draftKey, ...rest } = checklist
      return {
        ...rest,
        order: index + 1,
        fk_appointment_type_id: appointmentTypeId,
        fk_sub_appointment_type_id: subAppointmentTypeId,
      }
    })
    const nonContextRows = allChecklists.filter((checklist) => !isMatchingContext(checklist))
    saveChanges.mutate({ checklists: [...nonContextRows, ...contextRows] })
  }

  return (
    <div className="mt-6">
      <SChecklistTitleRow>
        <h3 className="text-lg font-semibold">{tr("title")}</h3>
        <div className="flex items-center gap-2">
          <Button primary type="button" onClick={addChecklist}>
            <RiAddFill /> {tr("New")}
          </Button>
          <Button
            type="button"
            onClick={() => setShowExistingPicker((open) => !open)}
          >
            <RiAddFill /> {tr("Existing")}
          </Button>
        </div>
      </SChecklistTitleRow>
      <SFlatContent>
        <SChecklistToolbar ref={existingAnchorRef}>
          <SStickyHeader>
            <SHeaderCell aria-hidden />
            <SHeaderCell $narrow>#</SHeaderCell>
            <SHeaderCell>{tr("Label")}</SHeaderCell>
            <SHeaderCell $narrow>{tr("Required")}</SHeaderCell>
            <div className="flex items-center justify-self-end gap-2">
              <Button
                primary
                type="button"
                onClick={handleSaveAll}
                disabled={!isFormDirty || saveChanges.isLoading}
              >
                {tr("Save")}
              </Button>
            </div>
          </SStickyHeader>
          <ChecklistExistingPicker
            open={showExistingPicker}
            onOpenChange={setShowExistingPicker}
            anchorRef={existingAnchorRef}
            appointmentTypeId={appointmentTypeId}
            subAppointmentTypeId={subAppointmentTypeId}
            variant="admin"
            onConfirm={copyFromExistingRows}
            translate={(key, options) => tr(key, options)}
          />
        </SChecklistToolbar>

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable
            droppableId={`checklist-rows-${appointmentTypeId}-${subAppointmentTypeId ?? "parent"}`}
          >
            {(droppableProvided) => (
              <div ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                {formik.values.checklists.map((checklist, index) => {
                  const isRowDirty = isChecklistRowDirty(checklist)
                  const isNewChecklist = checklist.id === null
                  const draggableId =
                    checklist.id != null
                      ? `id-${checklist.id}`
                      : `draft-${checklist.draftKey ?? index}`
                  return (
                    <Draggable key={draggableId} draggableId={draggableId} index={index}>
                      {(draggableProvided, snapshot) => (
                        <SChecklistRow
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                          style={{
                            ...draggableProvided.draggableProps.style,
                            background: snapshot.isDragging ? "#f9fafb" : undefined,
                          }}
                        >
                          <SDragHandle
                            {...draggableProvided.dragHandleProps}
                            aria-label={tr("Drag to reorder")}
                          >
                            <MdDragIndicator />
                          </SDragHandle>
                          <SRowIndex>{index + 1}</SRowIndex>
                          <Input
                            value={checklist.label || ""}
                            onChange={(event: any) =>
                              handleFieldChange(index, "label", event?.target?.value || "")
                            }
                            name={`checklists.${index}.label`}
                            style={getFluidInputStyle()}
                          />
                          <SRequiredWrap>
                            <Input
                              type="checkbox"
                              checked={checklist.required === true}
                              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                handleFieldChange(index, "required", event.target.checked)
                              }
                              name={`checklists.${index}.required`}
                              style={{ width: "16px", height: "16px" }}
                            />
                          </SRequiredWrap>
                          <SChecklistActions>
                            {isRowDirty ? (
                              <IconButton
                                type="button"
                                onClick={() => handleCancelRowChanges(index)}
                                style={isNewChecklist ? { color: "#6b7280" } : undefined}
                                disabled={saveChanges.isLoading}
                              >
                                {isNewChecklist ? (
                                  <RiCloseCircleFill />
                                ) : (
                                  <CgUndo size={24} className="text-blue-500" />
                                )}
                              </IconButton>
                            ) : (
                              <IconButton
                                type="button"
                                style={{ color: "#dc2626" }}
                                onClick={() => handleDeleteChecklist(index)}
                              >
                                <RiDeleteBin2Fill />
                              </IconButton>
                            )}
                          </SChecklistActions>
                        </SChecklistRow>
                      )}
                    </Draggable>
                  )
                })}
                {droppableProvided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </SFlatContent>
    </div>
  )
}

const SChecklistTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
`

const SChecklistRow = styled.div`
  display: grid;
  grid-template-columns: ${CHECKLIST_MAIN_GRID};
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 0.75rem;
  width: 100%;
  box-sizing: border-box;
`

const SDragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  cursor: grab;
  user-select: none;
  padding: 0.125rem;
  &:active {
    cursor: grabbing;
  }
  &:hover {
    color: #4b5563;
  }
`

const SRowIndex = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #6b7280;
  box-sizing: border-box;
  padding: 0.125rem;
`

const SStickyHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 5;
  display: grid;
  grid-template-columns: ${CHECKLIST_MAIN_GRID};
  gap: 0.5rem;
  align-items: center;
  padding: 0.5rem 0;
  margin-bottom: 0.5rem;
  background: white;
  border-bottom: 1px solid #e5e7eb;
  width: 100%;
  box-sizing: border-box;
`

const SHeaderCell = styled.div<{ $narrow?: boolean }>`
  font-size: 0.8rem;
  font-weight: 600;
  color: #4b5563;
  box-sizing: border-box;
  min-width: 0;
  padding: 0.125rem;
  ${(p) =>
    p.$narrow
      ? `
    text-align: center;
    justify-self: stretch;
  `
      : ""}
`

const SChecklistActions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  min-width: 0;
`

const SChecklistToolbar = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 0.5rem;
`

const SRequiredWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  padding: 0.125rem;
`

export default Checklists
